import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { CheckCircle, XCircle, Clock, Download, Eye, EyeOff } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Badge } from '../../components/ui/badge'
import ErrorDisplay from '../../components/common/ErrorDisplay'
import { useStudentExams } from '../../hooks/useStudentExams'
import { examService } from '../../services/examService'
import { useAuth } from '../../context/AuthContext'
import LoadingSpinner from '../../components/common/LoadingSpinner'

const ExamResults = () => {
  const { examId } = useParams()
  const { user } = useAuth()
  const isStaff = user?.role === 'staff'
  const [showAnswers, setShowAnswers] = useState(false)
  const [examResults, setExamResults] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  const { 
    examResults: studentExamResults, 
    loading: studentLoading, 
    error: studentError, 
    fetchExamResult 
  } = useStudentExams()

  useEffect(() => {
    const fetchResults = async () => {
      if (!examId) return;
      
      try {
        setLoading(true)
        setError(null)
        
        if (isStaff) {
          // For staff, fetch all results for the exam
          const response = await examService.getExamResults(examId)
          // Transform the response to match the expected format
          // Staff will see a list of results, not a single result
          const resultsData = response.data?.data?.results || response.data?.data?.data?.results || []
          // For now, show the first result or create a summary view
          if (resultsData.length > 0) {
            setExamResults(resultsData[0])
          } else {
            setExamResults(null)
          }
        } else {
          // For students, use the existing hook
          await fetchExamResult(examId)
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load exam results')
        console.error('Error fetching exam results:', err)
      } finally {
        setLoading(false)
      }
    }
    
    fetchResults()
  }, [examId, isStaff])
  
  // Update examResults when studentExamResults changes (for students)
  useEffect(() => {
    if (!isStaff && studentExamResults) {
      setExamResults(studentExamResults)
    }
  }, [studentExamResults, isStaff])

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A': return 'text-green-600'
      case 'B': return 'text-blue-600'
      case 'C': return 'text-yellow-600'
      case 'D': return 'text-orange-600'
      case 'F': return 'text-red-600'
      default: return 'text-gray-600'
    }
  }

  const getGradeBgColor = (grade) => {
    switch (grade) {
      case 'A': return 'bg-green-100'
      case 'B': return 'bg-blue-100'
      case 'C': return 'bg-yellow-100'
      case 'D': return 'bg-orange-100'
      case 'F': return 'bg-red-100'
      default: return 'bg-gray-100'
    }
  }

  const getQuestionTypeLabel = (type) => {
    switch (type) {
      case 'objective': return 'Multiple Choice'
      case 'theory': return 'Theory/Essay'
      case 'fill-in-gaps': return 'Fill in the Gaps'
      default: return type
    }
  }

  const handleDownloadResults = () => {
    // Mock PDF download
    console.log('Downloading results PDF...')
  }

  if (loading || (isStaff ? loading : studentLoading)) {
    return <LoadingSpinner />
  }

  if (error || studentError) {
    return <ErrorDisplay message={error || studentError} />
  }

  if (!examResults && !studentExamResults) {
    return <ErrorDisplay message="Exam results not found" />
  }

  const results = examResults || studentExamResults

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Exam Results</h1>
          <p className="text-gray-600 mt-1">{results?.examTitle || results?.exam?.title || 'Exam Results'}</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowAnswers(!showAnswers)}
            className="flex items-center gap-2"
          >
            {showAnswers ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showAnswers ? 'Hide Answers' : 'Show Answers'}
          </Button>
          <Button
            variant="outline"
            onClick={handleDownloadResults}
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </Button>
        </div>
      </div>

      {/* Overall Score */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className={`w-20 h-20 rounded-full ${getGradeBgColor(results?.grade)} flex items-center justify-center mx-auto mb-3`}>
                <span className={`text-2xl font-bold ${getGradeColor(results?.grade)}`}>
                  {results?.grade || 'N/A'}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-600">Grade</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl font-bold text-indigo-600">
                  {results?.percentage || results?.percentageScore || 0}%
                </span>
              </div>
              <p className="text-sm font-medium text-gray-600">Percentage</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl font-bold text-green-600">
                  {results?.marksObtained || results?.obtainedMarks || 0}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-600">Marks Obtained</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl font-bold text-gray-600">
                  {results?.totalMarks || results?.exam?.totalMarks || 0}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-600">Total Marks</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Performance Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-8 h-8 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {results?.summary?.objectives?.correct || 0}/{results?.summary?.objectives?.total || 0}
              </p>
              <p className="text-sm text-gray-600">Multiple Choice Correct</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Clock className="w-8 h-8 text-yellow-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {results?.summary?.theory?.marks || 0}
              </p>
              <p className="text-sm text-gray-600">Theory Marks</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {results?.summary?.fillInGaps?.correct || 0}/{results?.summary?.fillInGaps?.total || 0}
              </p>
              <p className="text-sm text-gray-600">Fill-in-Gaps Correct</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Question-by-Question Review */}
      <Card>
        <CardHeader>
          <CardTitle>Question Review</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {(results?.questions || []).map((question, index) => (
              <Card key={question.id} className="border-l-4 border-l-indigo-500">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-semibold">Question {index + 1}</h4>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">
                          {getQuestionTypeLabel(question.type)}
                        </Badge>
                        <Badge variant="outline">
                          {question.awardedMarks}/{question.marks} marks
                        </Badge>
                        {question.isCorrect !== undefined && (
                          <Badge variant={question.isCorrect ? 'success' : 'destructive'}>
                            {question.isCorrect ? (
                              <CheckCircle className="w-3 h-3 mr-1" />
                            ) : (
                              <XCircle className="w-3 h-3 mr-1" />
                            )}
                            {question.isCorrect ? 'Correct' : 'Incorrect'}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div>
                      <p className="text-gray-900 mb-3">{question.question}</p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-medium text-gray-700 mb-2">Your Answer:</p>
                      <p className="text-gray-900">
                        {question.type === 'objective' ? question.studentAnswer : question.studentAnswer}
                      </p>
                    </div>

                    {showAnswers && question.correctAnswer && (
                      <div className="bg-green-50 p-4 rounded-lg">
                        <p className="font-medium text-green-900 mb-2">Correct Answer:</p>
                        <p className="text-green-800">{question.correctAnswer}</p>
                      </div>
                    )}

                    {question.teacherFeedback && (
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="font-medium text-blue-900 mb-2">Teacher Feedback:</p>
                        <p className="text-blue-800">{question.teacherFeedback}</p>
                      </div>
                    )}

                    {question.rubric && (
                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <p className="font-medium text-yellow-900 mb-2">Marking Rubric:</p>
                        <p className="text-yellow-800 text-sm">{question.rubric}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Exam Details */}
      <Card>
        <CardHeader>
          <CardTitle>Exam Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Student Information</h4>
              <p className="text-gray-600">Name: {results?.studentName || results?.student?.firstName + ' ' + results?.student?.lastName || 'N/A'}</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Submission Details</h4>
              <p className="text-gray-600">
                Submitted: {results?.submittedAt ? new Date(results.submittedAt).toLocaleString() : 'N/A'}
              </p>
              <p className="text-gray-600">
                Graded: {results?.gradedAt ? new Date(results.gradedAt).toLocaleString() : 'Not graded'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default ExamResults